import React, { useLayoutEffect, useRef, useState } from "react";
import { DESKTOP_FIT_QUERY, useDesktopFit } from "@/features/session-diagnostics/hooks/use-desktop-fit";

type Size = { width: number; height: number };

// vb-metrics#38 owner ruling: on a desktop the whole session view fits the screen height, so the recorded app is
// shrunk to the room it has, never cropped. The scale is a CSS transform on a wrapper: the replay inside (and its
// tap marks, drawn in the replay's own coordinates) is exactly what the user saw, only smaller.
export { DESKTOP_FIT_QUERY };

// `max` is how far the content may grow (1 = never above its own size).
export const fitScale = ({ availableW, availableH, recordedW, recordedH, max = 1 }: { availableW: number; availableH: number; recordedW: number; recordedH: number; max?: number }): number => {
    if (recordedW <= 0 || recordedH <= 0 || availableW <= 0 || availableH <= 0) return 1;
    return Math.min(max, availableH / recordedH, availableW / recordedW);
};

type Props = {
    // The frame's size before the first measurement (from the recording's viewport), so the layout does not jump.
    estimate: Size;
    children: React.ReactNode;
};

export const ReplayStage: React.FC<Props> = ({ estimate, children }) => {
    const stage = useRef<HTMLDivElement>(null);
    const content = useRef<HTMLDivElement>(null);
    const [room, setRoom] = useState<Size | null>(null);
    const [natural, setNatural] = useState<Size | null>(null);
    const boundByHeight = useDesktopFit();

    // ResizeObserver reports layout sizes, which a transform does not change: the content's natural size stays
    // measurable while it is drawn scaled.
    useLayoutEffect(() => {
        const stageNode = stage.current;
        const contentNode = content.current;
        if (stageNode === null || contentNode === null || typeof ResizeObserver !== "function") return;
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const size = { width: entry.contentRect.width, height: entry.contentRect.height };
                if (entry.target === stageNode) setRoom(size);
                if (entry.target === contentNode) setNatural(size);
            }
        });
        observer.observe(stageNode);
        observer.observe(contentNode);
        return () => observer.disconnect();
    }, []);

    const recorded = natural !== null && natural.width > 0 && natural.height > 0 ? natural : estimate;
    const scale = room === null ? 1 : fitScale({
        availableW: room.width,
        // Stacked (narrow or short screen): the page scrolls, so only the width bounds the replay.
        availableH: boundByHeight ? room.height : Number.POSITIVE_INFINITY,
        recordedW: recorded.width,
        recordedH: recorded.height,
    });

    return (
        <div ref={stage} className="sd-stage" data-testid="replay-stage">
            <div className="sd-stage__reserve" style={{ width: recorded.width * scale, height: recorded.height * scale }}>
                <div ref={content} className="sd-stage__scaled" data-testid="replay-scaled" style={{ transform: `translateX(-50%) scale(${scale})` }}>
                    {children}
                </div>
            </div>
        </div>
    );
};
